import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Leaf, Loader2, Store, Bike, Shield, User, Headset } from "lucide-react";
import { toast } from "sonner";

const DEMOS = [
  { role: "Admin", email: "itwingspune@gmail.com", pass: "Admin@123", icon: Shield, to: "/admin", color: "#8B5CF6" },
];

const dest = (role) => ({ retailer: "/retailer", delivery: "/delivery", admin: "/admin", sub_admin: "/admin", support_agent: "/agent", survey_agent: "/survey", influencer: "/affiliate" }[role] || "/");

export default function Login() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.name.split(" ")[0]}!`);
      nav(dest(user.role));
    } catch (err) { toast.error(apiError(err)); }
    setLoading(false);
  };

  const demoLogin = async (d) => {
    setLoading(true);
    try { const user = await login(d.email, d.pass); toast.success(`Logged in as ${d.role}`); nav(dest(user.role)); }
    catch (err) { toast.error(apiError(err)); }
    setLoading(false);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-brand-deep grain p-4">
      <div className="relative z-10 w-full max-w-md rounded-3xl bg-white p-8 shadow-2xl">
        <Link to="/" className="mb-6 flex items-center">
          <img src="/logo.png" alt="Grofkart" className="h-9 w-auto" />
        </Link>
        <h1 className="font-head text-2xl font-extrabold text-slate-900">Welcome back</h1>
        <p className="mb-6 text-sm text-slate-500">Login to continue shopping</p>

        <form onSubmit={submit} className="space-y-3">
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email address"
            data-testid="login-email" className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-brand" />
          <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password"
            data-testid="login-password" className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-brand" />
          <button type="submit" disabled={loading} data-testid="login-submit"
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand py-3 font-bold text-white transition-colors hover:bg-brand-deep disabled:opacity-60">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />} Login
          </button>
        </form>

        <div className="mt-3 text-center">
          <Link to="/forgot-password" data-testid="forgot-password-link" className="text-sm font-semibold text-brand hover:underline">Forgot password?</Link>
        </div>

        <p className="mt-4 text-center text-sm text-slate-500">
          New to Grofkart? <Link to="/register" className="font-semibold text-brand">Create account</Link>
        </p>

        <div className="mt-6 border-t border-slate-100 pt-4">
          <p className="mb-3 text-center text-xs font-semibold uppercase tracking-wide text-slate-400">Administrator access</p>
          <div className="grid grid-cols-1 gap-2">
            {DEMOS.map((d) => (
              <button key={d.role} onClick={() => demoLogin(d)} data-testid={`demo-login-${d.role.toLowerCase()}`}
                className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2.5 text-left transition-all hover:-translate-y-0.5 hover:shadow-md">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg" style={{ background: `${d.color}18`, color: d.color }}><d.icon className="h-4 w-4" /></div>
                <div className="min-w-0">
                  <p className="text-xs font-bold text-slate-800">{d.role}</p>
                  <p className="truncate text-[10px] text-slate-400">Demo</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
